import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuthStore, useCartStore } from '../lib/store';
import { formatPrice } from '../lib/utils';
import { ShoppingBag, Check, ArrowLeft, Minus, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductDetailPage = ({ onAuthClick }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [added, setAdded] = useState(false);
  
  const { isAuthenticated } = useAuthStore();
  const { addToCart } = useCartStore();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API}/products/${id}`);
        setProduct(res.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      onAuthClick?.();
      return;
    }

    setIsAdding(true);
    const success = await addToCart(product.id, quantity);
    setIsAdding(false);
    
    if (success) {
      setAdded(true);
      toast.success(`${quantity}x ${product.name} added to cart`);
      setTimeout(() => setAdded(false), 2000);
    } else {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="paper-texture min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate('/products')} className="mb-8" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="bg-surface rounded-xl overflow-hidden">
            <img 
              src={product.image_url} 
              alt={product.name}
              className="w-full h-full object-contain p-6 aspect-square"
              data-testid="product-image"
            />
          </div>

          {/* Details */}
          <div className="flex flex-col">
            <span className="font-accent text-2xl text-primary">{product.category}</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-secondary mt-2" data-testid="product-name">
              {product.name}
            </h1>
            
            <div className="flex items-center gap-4 mt-6">
              <span className="text-3xl font-bold text-primary" data-testid="product-price">{formatPrice(product.price)}</span>
              {product.in_stock ? (
                <span className="bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-medium">In Stock</span>
              ) : (
                <span className="bg-destructive/10 text-destructive px-3 py-1 rounded-full text-sm font-medium">Out of Stock</span>
              )}
            </div>

            <p className="text-muted-foreground mt-6 text-lg leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Quantity selector */}
            <div className="flex items-center gap-4 mt-8">
              <span className="text-secondary font-medium">Quantity:</span>
              <div className="flex items-center border border-border rounded-md">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  data-testid="decrease-quantity"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-12 text-center font-medium" data-testid="quantity-value">{quantity}</span>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="increase-quantity"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Add to cart */}
            <Button 
              size="lg" 
              className="mt-8 text-lg"
              onClick={handleAddToCart}
              disabled={isAdding || !product.in_stock}
              data-testid="add-to-cart-button"
            >
              {isAdding ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : added ? (
                <Check className="w-5 h-5 mr-2" />
              ) : (
                <ShoppingBag className="w-5 h-5 mr-2" />
              )}
              {added ? 'Added to Cart!' : product.in_stock ? 'Add to Cart' : 'Out of Stock'}
            </Button>

            {/* Product details */}
            <div className="mt-12 border-t pt-8">
              <h3 className="font-heading text-xl font-semibold text-secondary mb-4">Product Details</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  500ml bottle (16.9 fl oz)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  Organic ingredients
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  Raw, unfiltered, unpasteurized
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  No artificial preservatives
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  Vegan friendly
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
